import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAppInfo } from "./redux/downloadAppSlice";
import CardComponent from "./CardComponent";
import { AppState } from "../config/redux/reducers";
import { useStyles } from "./downloadApp.styles";
import { Box, Grid, Typography } from "@material-ui/core";
import DownloadAppConstants from "../constants/DownloadAppConstants";
import SelectBox from "common/SelectBox";
import Loader from "common/Loader";
import { TextConstants } from "constants/TextConstants";

interface Build {
  pageUrl: string;
  downloadUrl: string;
  version: string;
  build: string;
  releaseNotes: string;
  size: number;
  uploadedAt: string;
}

interface TransformedData {
  domain: string;
  appId: string;
  appName: string;
  appDesc: string;
  platform: string;
  versions: string[];
  data: Record<string, Build[]>; // Data grouped by version
}

const DownloadApp = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { loading, appData } = useSelector(
    (state: AppState) => state.DownloadApp
  );

  const [revealedUrl, setRevealedUrl] = useState<string>("");
  const [filters, setFilters] = useState(
    useMemo(
      () =>
        DownloadAppConstants.filters.reduce((acc: any, filter: any) => {
          acc[filter.key] = filter.options[0]?.value || "";
          return acc;
        }, {}),
      []
    )
  );

  useEffect(() => {
    dispatch(fetchAppInfo({ filters }));
  }, [dispatch, filters]);

  const handleReveal = (url: string) => {
    setRevealedUrl(url);
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    setFilters((prevFilters: Record<string, string>) => ({
      ...prevFilters,
      [filterKey]: value,
    }));
  };

  return (
    <div className={classes.root}>
      {loading && <Loader></Loader>}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" gutterBottom className={classes.title}>
         {TextConstants.APP_DOWNLOAD_PG_TITLE}
        </Typography>
        <Box display="flex" style={{ gap: 4, marginRight: "2%", marginBottom: 16 }} mx={2}>
          {DownloadAppConstants.filters.map((filter) => (
            <SelectBox
              key={filter.key}
              label={filter.label}
              items={filter.options}
              enableSearch={false}
              value={filters[filter.key]}
              handleChange={(value: string) =>
                handleFilterChange(filter.key, value)
              }
            />
          ))}
        </Box>
      </Box>
      <Grid container spacing={3} className={classes.container}>
        {appData.map((app: TransformedData, index: number) => (
          <Grid
            item
            key={index}
            xs={12} // Full width on small screens
            sm={6} // Two cards per row on medium screens
            md={4}
          >
            <CardComponent
              title={app.appName}
              versions={app.versions}
              appDesc={app.appDesc}
              domain={app.domain}
              data={app.data}
              onReveal={handleReveal}
              revealedUrl={revealedUrl}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default DownloadApp;
